import PropTypes from 'prop-types';
import React from 'react';
import {FormattedMessage} from 'react-intl';

import VM from '@scratch/scratch-vm';
import {legacyConfig} from '../../legacy-config';
import SpriteLibrary from '../../containers/sprite-library.jsx';
import SpriteSelectorComponent from '../sprite-selector/sprite-selector.jsx';
import StageSelector from '../../containers/stage-selector.jsx';
import HardwareDevicePane from '../../containers/hardware-device-pane.jsx';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants';

import styles from './target-pane.css';

/*
 * Pane that contains the sprite selector, sprite info, stage selector,
 * and the new sprite, costume and backdrop buttons
 * @param {object} props Props for the component
 * @returns {React.Component} rendered component
 */
const TargetPane = ({
    activePane,
    editingTarget,
    fileInputRef,
    hardwareDeviceCount,
    hoveredTarget,
    spriteLibraryVisible,
    onActivateBlocksTab,
    onActivateDevicesPane,
    onActivateTargetsPane,
    onChangeSpriteDirection,
    onChangeSpriteName,
    onChangeSpriteRotationStyle,
    onChangeSpriteSize,
    onChangeSpriteVisibility,

    onChangeSpriteX,
    onChangeSpriteY,
    onDeleteSprite,
    onDrop,
    onDuplicateSprite,
    onExportSprite,
    onFileUploadClick,
    onNewBackdropClick,
    onNewSpriteClick,
    onPaintSpriteClick,
    onRequestCloseSpriteLibrary,
    onSelectSprite,
    onSpriteUpload,
    onSurpriseSpriteClick,
    raiseSprites,
    stage,
    stageSize,
    sprites,
    vm,
    ...componentProps
}) => (
    <div
        className={styles.targetPane}
        {...componentProps}
    >

        <div className={styles.paneTabs}>
            <button
                aria-pressed={activePane === 'devices'}
                className={activePane === 'devices' ? styles.activeTab : null}
                type="button"
                onClick={onActivateDevicesPane}
            >
                <FormattedMessage
                    defaultMessage="设备"
                    description="Tab that shows connected hardware devices"
                    id="gui.targetPane.devicesTab"
                />
                {hardwareDeviceCount ? <span className={styles.deviceCount}>{hardwareDeviceCount}</span> : null}
            </button>
            <button
                aria-pressed={activePane === 'targets'}
                className={activePane === 'targets' ? styles.activeTab : null}
                type="button"
                onClick={onActivateTargetsPane}
            >
                <FormattedMessage
                    defaultMessage="角色和背景"
                    description="Tab that shows sprites and the stage"
                    id="gui.targetPane.targetsTab"
                />
            </button>
        </div>

        <div className={styles.paneContent}>
            {activePane === 'devices' ? (
                <HardwareDevicePane vm={vm} />
            ) : (
                <React.Fragment>
                    <SpriteSelectorComponent
                        editingTarget={editingTarget}
                        hoveredTarget={hoveredTarget}
                        raised={raiseSprites}
                        selectedId={editingTarget}
                        spriteFileInput={fileInputRef}
                        sprites={sprites}
                        stageSize={stageSize}
                        onChangeSpriteDirection={onChangeSpriteDirection}
                        onChangeSpriteName={onChangeSpriteName}
                        onChangeSpriteRotationStyle={onChangeSpriteRotationStyle}
                        onChangeSpriteSize={onChangeSpriteSize}
                        onChangeSpriteVisibility={onChangeSpriteVisibility}
                        onChangeSpriteX={onChangeSpriteX}
                        onChangeSpriteY={onChangeSpriteY}
                        onDeleteSprite={onDeleteSprite}
                        onDrop={onDrop}
                        onDuplicateSprite={onDuplicateSprite}
                        onExportSprite={onExportSprite}
                        onFileUploadClick={onFileUploadClick}
                        onNewSpriteClick={onNewSpriteClick}
                        onPaintSpriteClick={onPaintSpriteClick}
                        onSelectSprite={onSelectSprite}
                        onSpriteUpload={onSpriteUpload}
                        onSurpriseSpriteClick={onSurpriseSpriteClick}
                    />
                    <div className={styles.stageSelectorWrapper}>
                        {stage.id && <StageSelector
                            asset={
                                stage.costume &&
                                stage.costume.asset
                            }
                            backdropCount={stage.costumeCount}
                            id={stage.id}
                            selected={stage.id === editingTarget}
                            onSelect={onSelectSprite}
                            onNewBackdropClick={onNewBackdropClick}
                        />}
                        <div>
                            {spriteLibraryVisible ? (
                                <SpriteLibrary
                                    vm={vm}
                                    onActivateBlocksTab={onActivateBlocksTab}
                                    onRequestClose={onRequestCloseSpriteLibrary}
                                />
                            ) : null}
                        </div>
                    </div>
                </React.Fragment>
            )}
        </div>
    </div>
);

const spriteShape = PropTypes.shape({
    costume: PropTypes.shape({
        url: PropTypes.string,
        name: PropTypes.string.isRequired,
        asset: PropTypes.instanceOf(legacyConfig.storage.scratchStorage.Asset),
        // The following are optional because costumes uploaded from disk
        // will not have these properties available
        bitmapResolution: PropTypes.number,
        rotationCenterX: PropTypes.number,
        rotationCenterY: PropTypes.number
    }),
    costumeCount: PropTypes.number,
    direction: PropTypes.number,
    id: PropTypes.string,
    name: PropTypes.string,
    order: PropTypes.number,
    size: PropTypes.number,
    visibility: PropTypes.bool,
    x: PropTypes.number,
    y: PropTypes.number
});

TargetPane.propTypes = {
    activePane: PropTypes.oneOf(['devices', 'targets']).isRequired,
    editingTarget: PropTypes.string,
    extensionLibraryVisible: PropTypes.bool,
    fileInputRef: PropTypes.func,
    hardwareDeviceCount: PropTypes.number.isRequired,
    hoveredTarget: PropTypes.shape({
        hoveredSprite: PropTypes.string,
        receivedBlocks: PropTypes.bool
    }),
    onActivateBlocksTab: PropTypes.func.isRequired,
    onActivateDevicesPane: PropTypes.func.isRequired,
    onActivateTargetsPane: PropTypes.func.isRequired,
    onChangeSpriteDirection: PropTypes.func,
    onChangeSpriteName: PropTypes.func,
    onChangeSpriteRotationStyle: PropTypes.func,
    onChangeSpriteSize: PropTypes.func,
    onChangeSpriteVisibility: PropTypes.func,
    onChangeSpriteX: PropTypes.func,
    onChangeSpriteY: PropTypes.func,
    onDeleteSprite: PropTypes.func,
    onDrop: PropTypes.func,
    onDuplicateSprite: PropTypes.func,
    onExportSprite: PropTypes.func,
    onFileUploadClick: PropTypes.func,
    onNewBackdropClick: PropTypes.func,
    onNewSpriteClick: PropTypes.func,
    onPaintSpriteClick: PropTypes.func,
    onRequestCloseExtensionLibrary: PropTypes.func,
    onRequestCloseSpriteLibrary: PropTypes.func,
    onSelectSprite: PropTypes.func,
    onSpriteUpload: PropTypes.func,
    onSurpriseSpriteClick: PropTypes.func,
    raiseSprites: PropTypes.bool,
    spriteLibraryVisible: PropTypes.bool,
    sprites: PropTypes.objectOf(spriteShape),
    stage: spriteShape,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    vm: PropTypes.instanceOf(VM)
};

export default TargetPane;
